import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import eCosicon from "../icon/eCos-icon.png";
import login_image from '../image/login_image.jpg'; // Background image
import Link from '@mui/material/Link';
// API
import { requestPasswordReset } from '../api/auth';

const PasswordResetRequest = () => {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [error, setError] = useState({ username: '', email: '', general: '' });
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError({ username: '', email: '', general: '' }); // Clear previous errors
    setIsLoading(true);

    // Basic validation
    if (!username) {
      setError((prev) => ({ ...prev, username: 'Username is required' }));
      setIsLoading(false);
      return;
    }
    if (!email) {
      setError((prev) => ({ ...prev, email: 'Email is required' }));
      setIsLoading(false);
      return;
    }

    try {
      const response = await requestPasswordReset(email, username);
      setMessage(response.data.message);
      setIsLoading(false);

      // Redirect to a confirmation page after 3 seconds
      setTimeout(() => navigate('/reset-confirmation'), 3000);
    } catch (err) {
      setError((prev) => ({
        ...prev,
        general: err.response?.data?.error || 'An error occurred. Please try again.',
      }));
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundImage: `url(${login_image})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <Box
        sx={{
          p: 4,
          width: { xs: "350px", md: "400px" },
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          height: '100%',
          borderRadius: 2,
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          boxShadow: 3,
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: 'auto',
            mb: 3,
          }}
        >
          <img
            src={eCosicon}
            alt="eCos Icon"
            style={{
              width: '25%',
              height: '25%',
              objectFit: 'cover',
            }}
          />
        </Box>

        <Typography
          sx={{
            fontFamily: 'Arial, sans-serif',
            fontWeight: 'bold',
            fontStyle: 'italic',
            color: "black",
            fontSize: { xs: "4vw", md: "2vw" },
          }}
        >
          Reset Password
        </Typography>

        <Box
          component="form"
          onSubmit={handleSubmit}
          noValidate
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            width: '75%',
            mt: 1,

          }}
        >
          {/* Username Field */}
          <TextField
            label="Username"
            fullWidth
            variant="outlined"
            margin="normal"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            error={!!error.username}
            helperText={error.username}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              borderRadius: 2,
            }}
          />

          {/* Email Field */}
          <TextField
            label="Email"
            fullWidth
            variant="outlined"
            margin="normal"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={!!error.email}
            helperText={error.email}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              borderRadius: 2,
            }}
          />

          {/* General Error Message */}
          {error.general && (
            <Typography color="error" variant="body2" mt={1}>
              {error.general}
            </Typography>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{
              backgroundColor: 'purple',
              mt: 2,
              mb: 2,
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                backgroundColor: '#DEBBFF',
                transform: 'scale(1.05)',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
              },
            }}
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
          </Button>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/')}
            sx={{ display: 'block', mb: 2 }}
          >
            Back
          </Link>
        </Box>

        {/* Message */}
        {message && (
          <Typography
            sx={{
              mt: 2,
              color: '#4B5563',
              fontSize: '0.875rem',
              textAlign: 'center',
            }}
          >
            {message}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default PasswordResetRequest;